import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../../components/ui/VehicleServiceHero';
import { CheckCircle, Wrench, Shield, Clock, DollarSign, Award, Car, Settings, AlertTriangle, Star, ChevronRight, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import RelatedInventory from '../../../../components/RelatedInventory';

const AudiService = () => {
  const audiFeatures = [
    'Quattro System Experts',
    'VCDS Diagnostics',
    'Expert Technicians',
    '12-Month/12,000-Mile Warranty',
    'All Audi Models Serviced',
    'DSG & S-Tronic Specialists'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const models = {
    sedans: ['A3/S3/RS3', 'A4/S4/RS4', 'A5/S5/RS5', 'A6/S6/RS6', 'A7/S7/RS7', 'A8/S8'],
    suvs: ['Q3/RSQ3', 'Q5/SQ5', 'Q7/SQ7', 'Q8/SQ8/RSQ8', 'e-tron/e-tron GT']
  };

  return (
    <>
      <Helmet>
        <title>Audi Service Dallas | Audi Repair Dallas TX | Paradise-Performance</title>
        <meta name="description" content="Audi service in Dallas that doesn't cost a fortune. We work on Audis all day—quattro systems, DSG transmissions, check engine lights. Real mechanics who know these cars. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/european-car-repair/audi" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Audi Repair Dallas - Expert German Auto Service" />
        <meta property="og:description" content="Specialized Audi repair in Dallas. Expert technicians, genuine parts, advanced diagnostics. Save 30-40% vs dealership prices." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/services/european-car-repair/audi" />
        
        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Paradise-Performance - Audi Repair Dallas",
            "description": "Expert Audi repair and service in Dallas, TX",
            "url": "https://paradise-performance.com/services/european-car-repair/audi",
            "telephone": "+18173822316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": ["Dallas", "Fort Worth", "Plano", "Irving", "Arlington"],
            "priceRange": "$$"
          })}
        </script>

        {/* FAQ Schema for Featured Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does Audi maintenance cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Audi maintenance costs average $987 to $1,500 per year depending on the model. An oil change runs $120-$160 at an independent shop (vs $180-$280 at dealerships). DSG transmission service costs $350-$500. At Paradise-Performance near Dallas, we're typically 30-40% less than Audi dealership prices."
                }
              },
              {
                "@type": "Question",
                "name": "What is the Audi maintenance schedule?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Audi recommends service every 10,000 miles or 12 months. Key intervals: Oil change every 10,000 miles, DSG/S-tronic transmission service every 40,000 miles, spark plugs every 60,000 miles, brake fluid every 2 years, and coolant every 5 years or 100,000 miles."
                }
              },
              {
                "@type": "Question",
                "name": "Are Audis expensive to maintain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Audis cost more to maintain than mainstream brands but less than you might think if you skip the dealership. Annual maintenance runs $800-$1,200 at an independent specialist vs $1,500-$2,500 at an Audi dealer. The quattro system and DSG transmissions require specialized service but are very reliable when properly maintained."
                }
              },
              {
                "@type": "Question",
                "name": "What are common Audi problems?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Common Audi issues include: Carbon buildup on intake valves (2.0T FSI engines), timing chain tensioner failure (older 2.0T), DSG mechatronic unit issues, thermostat housing leaks (2.0T/3.0T), and PCV valve failures. Most can be prevented with regular maintenance using the correct VW 502.00 or 504.00 spec oil."
                }
              },
              {
                "@type": "Question",
                "name": "How long is the Audi warranty?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "New Audi vehicles come with a 4-year/50,000-mile limited warranty. Powertrain is included in this coverage. Corrosion perforation is covered for 12 years with unlimited mileage. Audi Care prepaid maintenance packages cover scheduled maintenance for the first 2-4 years."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="Audi"
        brandColor="#D5001C"
        tagline="Audi Repair & Service"
        description="We work on Audis all day. Quattro systems, DSG transmissions, check engine lights—whatever your Audi needs. Way cheaper than the dealership."
        features={audiFeatures}
        heroImage="/assets/assets/images/audi/audi.webp"
        heroImageAlt="Audi RS7 Performance Vehicle"
        heroVariant="default"
      />

      {/* Main Content */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          
          {/* Emergency Promo Box */}
          <motion.div {...fadeInUp} className="bg-red-50 border-2 border-red-600 rounded-lg p-6 max-w-4xl mx-auto mb-12">
            <h3 className="text-2xl font-bold text-red-700 mb-2 text-center">🚨 Limited Same-Day Appointments Available</h3>
            <p className="text-center text-gray-700 mb-4">
              Call <strong className="text-red-600">(817) 382-2316</strong> now for priority Audi diagnostics
            </p>
            <div className="text-center">
              <Link to="/contact" className="inline-block bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700 transition">
                Book Emergency Repair →
              </Link>
            </div>
          </motion.div>

          {/* Introduction Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">Audi Service Dallas</h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div>
                <img 
                  src="/assets/assets/images/new/euro-repair-img/Audi-repair.webp" 
                  alt="Audi repair facility in Paradise, TX"
                  className="rounded-lg shadow-lg w-full h-auto"
                />
              </div>
              <div className="flex flex-col justify-center">
                <h2 className="text-2xl font-bold mb-4">Audi Service for Dallas</h2>
                <p className="text-gray-700 mb-4">
                  We work on Audis all day. A4s, Q5s, A6s, whatever you've got. The quattro system, the DSG transmission, the check engine lights that won't go away—we've seen it all a hundred times.
                </p>
                <p className="text-gray-700 mb-4">
                  Yeah, we're in Paradise, not Dallas. But it's only 30 minutes, and once you see what the dealership was charging you, you'll understand why people make the drive.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" />15+ Years Audi Experience</li>
                  <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" />Professional VCDS Diagnostics</li>
                  <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" />OEM & Performance Parts</li>
                  <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" />12-Month/12,000-Mile Warranty</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Comprehensive Service Grid */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What We Do for Audis</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {/* Maintenance Services */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <Settings className="w-8 h-8 text-red-600 mr-3" />
                  <h3 className="text-xl font-bold">Scheduled Maintenance</h3>
                </div>
                <p className="text-gray-700 mb-4">The regular stuff your Audi needs to keep running</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Oil & filter service (5W-40/0W-40)</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Brake fluid flush (every 2 years)</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Spark plug replacement</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Air & cabin filter service</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Coolant system service</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Multi-point inspection</li>
                </ul>
              </div>

              {/* Repair Services */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <Wrench className="w-8 h-8 text-red-600 mr-3" />
                  <h3 className="text-xl font-bold">Expert Repairs</h3>
                </div>
                <p className="text-gray-700 mb-4">When something breaks, we fix it right</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Engine diagnostics & repair</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Transmission service (DSG/S-Tronic)</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Quattro system repairs</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Suspension & steering</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Electrical system diagnosis</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />A/C system service</li>
                </ul>
              </div>

              {/* Performance Services */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <Award className="w-8 h-8 text-red-600 mr-3" />
                  <h3 className="text-xl font-bold">Performance Solutions</h3>
                </div>
                <p className="text-gray-700 mb-4">Make your Audi faster (if that's your thing)</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Stage 1-3 ECU tuning</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Performance exhaust systems</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Cold air intake upgrades</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Suspension lowering/coilovers</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Big brake kit installation</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />RS model conversions</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Common Audi Repair Needs */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Common Audi Problems We Fix</h2>
            <p className="text-lg text-gray-700 mb-8">
              Dallas heat is brutal on Audis. Here's what we see all the time:
            </p>
            
            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-red-600">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Quattro System Issues</h3>
                <p className="text-gray-700">
                  The quattro system is great until it's not. Differential fluid gets old, transfer case starts making noise, electronic torque distribution goes haywire. We do the fluid services, fix the weird noises, and get your AWD working right again.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-red-600">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Carbon Buildup (Direct Injection Problems)</h3>
                <p className="text-gray-700">
                  Many Audi engines employ direct injection technology which, while excellent for performance and efficiency, can lead to carbon accumulation on intake valves. Dallas's stop-and-go traffic can accelerate this buildup. Our European auto repair center utilizes specialized walnut shell blasting and chemical treatments to restore proper airflow and performance.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-red-600">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">DSG and S tronic Transmission Services</h3>
                <p className="text-gray-700">
                  Audi's sophisticated dual-clutch transmissions require precise service procedures, specialized fluids, and expert calibration. Our <Link to="/services/transmission-repair" className="text-red-600 hover:underline">transmission repair</Link> specialists follow proper procedures for these complex units, helping Dallas drivers maintain the lightning-fast shifts and reliability that make Audi transmissions special. Looking for expert <Link to="/services/european-car-repair" className="text-red-600 hover:underline">European auto repair in Dallas</Link>? We're your Audi transmission experts.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-red-600">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Timing Chain System Maintenance</h3>
                <p className="text-gray-700">
                  Certain Audi engines utilize timing chain systems that require specialized preventative maintenance. Our European auto repair experts can identify early warning signs of timing chain wear and provide preventative service before catastrophic failure occurs – a particularly important service for Dallas drivers who may experience stop-and-go driving conditions.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Image Gallery Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="relative h-96 overflow-hidden rounded-lg shadow-2xl">
                <img 
                  src="/assets/assets/images/audi/audi-quattro.webp"
                  alt="Audi Quattro system repair"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">Quattro System Experts</h3>
                    <p className="text-white/90">Specialized service for Audi's legendary AWD technology</p>
                  </div>
                </div>
              </div>
              <div className="relative h-96 overflow-hidden rounded-lg shadow-2xl">
                <img 
                  src="/assets/assets/images/audi/audi-s-brakes.webp"
                  alt="Audi performance brake service"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">Performance Brake Service</h3>
                    <p className="text-white/90">S and RS model brake systems maintained to perfection</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Audi Maintenance Cost Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Audi Maintenance Cost in Dallas</h2>
            <p className="text-gray-700 mb-6">
              Audi ownership costs less than you might think—especially when you skip the dealership. Here's what common services cost at our shop vs the dealer:
            </p>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-red-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Service</th>
                    <th className="px-6 py-4 text-left">Dealership Price</th>
                    <th className="px-6 py-4 text-left">Our Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Oil Change (Full Synthetic)</td>
                    <td className="px-6 py-4 text-gray-600">$180 - $280</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$120 - $160</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">DSG/S-Tronic Transmission Service</td>
                    <td className="px-6 py-4 text-gray-600">$500 - $700</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$300 - $450</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Brake Pads & Rotors (Front)</td>
                    <td className="px-6 py-4 text-gray-600">$800 - $1,100</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$450 - $700</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Carbon Cleaning (Walnut Blast)</td>
                    <td className="px-6 py-4 text-gray-600">$800 - $1,200</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$500 - $750</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Timing Chain Tensioner</td>
                    <td className="px-6 py-4 text-gray-600">$2,500 - $4,000</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$1,800 - $2,800</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">Annual Inspection</td>
                    <td className="px-6 py-4 text-gray-600">$150 - $250</td>
                    <td className="px-6 py-4 text-green-600 font-semibold">$85 - $125</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-gray-600 text-sm italic">
              * Prices vary by model. S and RS models cost more. These are ballpark figures for A4, A6, Q5 and Q7 models.
            </p>
          </motion.div>

          {/* Models We Service */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Audi Models We Service</h2>
            <div className="bg-gray-100 rounded-lg p-8">
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-xl font-bold text-red-600 mb-4">Performance Sedans</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• A3, S3, RS3</li>
                    <li>• A4, S4, RS4 Avant</li>
                    <li>• A5, S5, RS5 Sportback</li>
                    <li>• A6, S6, RS6 Avant</li>
                    <li>• A7, S7, RS7 Sportback</li>
                    <li>• A8, S8</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-red-600 mb-4">SUVs & Crossovers</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Q3, RSQ3</li>
                    <li>• Q5, SQ5</li>
                    <li>• Q7, SQ7</li>
                    <li>• Q8, SQ8, RSQ8</li>
                    <li>• e-tron, e-tron GT</li>
                    <li>• Q4 e-tron</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-red-600 mb-4">Sports & Specialty</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• TT, TTS, TT RS</li>
                    <li>• R8 V10, R8 V10 Plus</li>
                    <li>• All Avant Models</li>
                    <li>• Allroad Quattro</li>
                    <li>• Classic RS Models</li>
                    <li>• e-tron GT, RS e-tron GT</li>
                  </ul>
                </div>
              </div>
              <p className="text-center text-gray-600 mt-6 italic">
                From the compact A3 to the flagship R8, we service all Audi models with factory-level expertise
              </p>
            </div>
          </motion.div>

          {/* Customer Testimonials */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What Dallas Audi Owners Say</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">
                  "After two Dallas-area shops couldn't properly diagnose my Audi S4's intermittent performance issue, 
                  I made the drive to Paradise-Performance. Their specialized diagnostic equipment immediately identified 
                  the problem with my Quattro system. The repair was efficient, properly explained, and has been perfect 
                  ever since. Well worth the short drive from Dallas!"
                </p>
                <p className="font-bold">— Jason M., Audi S4 Owner</p>
                <p className="text-sm text-gray-600">Dallas, TX</p>
              </div>
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">
                  "Paradise-Performance saved me thousands on my Q7's air suspension repair. The dealer quoted $8,500, 
                  but they fixed it for less than half that price using OEM parts. They even provided a loaner car 
                  while mine was being serviced. Best Audi service experience in the DFW area!"
                </p>
                <p className="font-bold">— Sarah K., Audi Q7 Owner</p>
                <p className="text-sm text-gray-600">Plano, TX</p>
              </div>
            </div>
          </motion.div>

          {/* Comparison Table */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Why Choose Paradise-Performance for Audi Service?</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-lg">
                <thead className="bg-red-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Service Factor</th>
                    <th className="px-6 py-4 text-left">Audi Dealership</th>
                    <th className="px-6 py-4 text-left">General Shop</th>
                    <th className="px-6 py-4 text-left">Paradise-Performance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 font-medium">VCDS Diagnostics</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                    <td className="px-6 py-4 text-red-600">✗</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-medium">Audi-Trained Techs</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                    <td className="px-6 py-4 text-red-600">✗</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium">OEM Parts Available</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                    <td className="px-6 py-4">Sometimes</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-medium">Service Cost</td>
                    <td className="px-6 py-4">$$$$$</td>
                    <td className="px-6 py-4">$$</td>
                    <td className="px-6 py-4 text-green-600">$$$ (30-40% less than dealer)</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium">Wait Time</td>
                    <td className="px-6 py-4">1-2 weeks</td>
                    <td className="px-6 py-4">2-3 days</td>
                    <td className="px-6 py-4 text-green-600">Same/Next day available</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-medium">Warranty</td>
                    <td className="px-6 py-4">12 months</td>
                    <td className="px-6 py-4">30-90 days</td>
                    <td className="px-6 py-4 text-green-600">12 months/12,000 miles</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3">How often should I service my Audi in Dallas?</h3>
                <p className="text-gray-700">
                  While Audi recommends 10,000-mile intervals, Dallas's extreme heat and stop-and-go traffic warrant more frequent service. 
                  We recommend oil changes every 5,000-7,500 miles, with full inspections at 10,000-mile intervals. This ensures optimal 
                  performance and prevents premature wear of critical components.
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3">Do you service Audi's Quattro all-wheel drive system?</h3>
                <p className="text-gray-700">
                  Absolutely. Our technicians are specially trained in Quattro system maintenance and repair. We perform differential fluid 
                  changes, Haldex pump services (where applicable), and complete system diagnostics. Proper Quattro maintenance is crucial 
                  for Dallas drivers who may encounter wet conditions or want optimal performance.
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3">Can you program and code Audi modules?</h3>
                <p className="text-gray-700">
                  Yes, we have factory-level VCDS (VAG-COM) diagnostic systems that allow us to perform coding, adaptations, and programming 
                  just like the dealership. Whether you need a new key programmed, want to enable hidden features, or require module replacement, 
                  we have the tools and expertise to handle it.
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3">What's the most common Audi repair you see from Dallas drivers?</h3>
                <p className="text-gray-700">
                  The most common issues we see are carbon buildup on direct-injection engines (especially 2.0T and 3.0T), DSG transmission 
                  services that have been neglected, and cooling system repairs due to Dallas heat stress. Regular maintenance and early 
                  intervention can prevent most of these issues from becoming major repairs.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Complete Audi Repair Services */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Complete Audi Repair Services for Dallas Enthusiasts</h2>
            <p className="text-lg text-gray-700 mb-8">
              Our European auto repair center provides comprehensive service for all Audi models, focusing on both maintenance and performance enhancement:
            </p>
            <ul className="grid md:grid-cols-2 gap-4 text-gray-700">
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />Scheduled maintenance following Audi specifications</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />Advanced electronics diagnostics and coding</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />Quattro system service and repair</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />Engine performance diagnostics and optimization</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />Turbocharger and supercharger service</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />DSG, S tronic, and multitronic transmission service</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />Timing belt and chain maintenance</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />Suspension system repair and upgrades</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />MMI system diagnostics and updates</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />Climate control system repair and recharge</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />Brake system upgrades and maintenance</li>
            </ul>
          </motion.div>

          {/* Audi Models We Service */}
          <motion.div {...fadeInUp} className="bg-white rounded-xl p-8 shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-8 text-gray-900">Audi Models We Service for Our Dallas Clients</h2>
            <p className="text-lg text-gray-700 mb-8">
              Our European auto repair Dallas facility has extensive experience with the full range of Audi models, including:
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Audi Sedans & Sportbacks</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>A3, S3, RS3</li>
                  <li>A4, S4, RS4</li>
                  <li>A5 Sportback, S5, RS5</li>
                  <li>A6, S6, RS6</li>
                  <li>A7, S7, RS7</li>
                  <li>A8, S8</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Audi SUVs & Crossovers</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>Q3, SQ3, RS Q3</li>
                  <li>Q5, SQ5</li>
                  <li>Q7, SQ7</li>
                  <li>Q8, SQ8, RS Q8</li>
                  <li>e-tron models</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Audi Coupes & Specialty</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>TT, TTS, TT RS</li>
                  <li>A5 Coupe, S5, RS5</li>
                  <li>R8</li>
                  <li>Allroad models</li>
                  <li>Classic Audi models</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Advanced Diagnostics Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-gray-900">Advanced Diagnostics for Audi Vehicles</h2>
            <p className="text-lg text-gray-700 mb-8">
              Modern Audi vehicles feature sophisticated electronic systems that require specialized diagnostic approaches. Our European auto repair center maintains professional diagnostic equipment specifically for Audi vehicles:
            </p>
            
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow">
                <thead>
                  <tr className="bg-red-600 text-white">
                    <th className="px-6 py-3 text-left">Diagnostic Capability</th>
                    <th className="px-6 py-3 text-left">Our European Auto Center</th>
                    <th className="px-6 py-3 text-left">General Repair Shops</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 font-medium">Full Audi Electronic Access</td>
                    <td className="px-6 py-4 text-green-600">Complete access to all control modules</td>
                    <td className="px-6 py-4 text-gray-600">Limited access to engine and transmission only</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-medium">Control Module Coding</td>
                    <td className="px-6 py-4 text-green-600">Advanced programming and adaptation</td>
                    <td className="px-6 py-4 text-gray-600">Basic code reading only</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium">Audi-Specific Testing</td>
                    <td className="px-6 py-4 text-green-600">Professional component testing</td>
                    <td className="px-6 py-4 text-gray-600">Generic testing procedures</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-medium">Software Updates</td>
                    <td className="px-6 py-4 text-green-600">Ability to update control modules</td>
                    <td className="px-6 py-4 text-gray-600">No update capabilities</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium">Technical Database</td>
                    <td className="px-6 py-4 text-green-600">Complete Audi technical specifications</td>
                    <td className="px-6 py-4 text-gray-600">Limited generic information</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* ODIS Diagnostics Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">ODIS Diagnostics for Audi — Dealer-Level Access, Independent Shop Prices</h2>
            <p className="text-lg text-gray-700 mb-8">
              Most independent shops run VCDS or generic OBD scanners. We run <strong>ODIS</strong> — the same Offboard Diagnostic Information System used at every Audi dealership worldwide. It's not cheap to license, and most shops don't bother. We did, because half the Audis that come through our door need something VCDS simply can't do.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img 
                  src="/assets/assets/images/Odis/audi-vw-odis-shop-1.webp"
                  alt="ODIS diagnostic software running on Audi at Paradise-Performance shop"
                  className="w-full h-72 object-cover"
                />
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img 
                  src="/assets/assets/images/Odis/audi-vw-odis-shop-3.webp"
                  alt="Audi connected to ODIS factory diagnostic system for module programming"
                  className="w-full h-72 object-cover"
                />
              </div>
            </div>

            <h3 className="text-2xl font-bold mb-4 text-gray-900">What ODIS Does That VCDS Can't</h3>
            <p className="text-gray-700 mb-6">
              VCDS is a solid tool for reading codes and basic adaptations. But newer Audis lock critical functions behind gateway security that only ODIS can bypass. If your shop can't get past the gateway, they can't fix the problem — period.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-red-600">
                <h4 className="text-lg font-bold mb-3 text-gray-900">Online Coding & SVM</h4>
                <p className="text-gray-700">
                  Swapping a control module on a 2019+ Audi? It needs online coding through Audi's SVM server. VCDS can't touch this. ODIS connects directly to Audi's backend, pulls the correct coding string, and flashes the new module. Without it, your replacement part is a paperweight.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-red-600">
                <h4 className="text-lg font-bold mb-3 text-gray-900">Component Protection</h4>
                <p className="text-gray-700">
                  Audi locks certain parts — instrument clusters, infotainment units, steering columns — with component protection. Only ODIS can release it. This is why shops quote you for a dealership tow when your used instrument cluster won't pair. We handle it in-house.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-red-600">
                <h4 className="text-lg font-bold mb-3 text-gray-900">ECU Flash Programming</h4>
                <p className="text-gray-700">
                  Software updates, campaign recalls, TCU recalibrations — all require ODIS flash capability. When Audi releases a transmission shift improvement or an emissions update, we can apply it the same day the dealership does.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-red-600">
                <h4 className="text-lg font-bold mb-3 text-gray-900">ADAS Calibration</h4>
                <p className="text-gray-700">
                  Replaced a windshield on your Audi with lane assist? The front camera needs recalibration through ODIS. Same goes for radar sensors after bumper work. Skip this step and your safety systems are guessing instead of measuring.
                </p>
              </div>
            </div>

            <div className="max-w-2xl mx-auto">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img 
                  src="/assets/assets/images/Odis/audi-vw-odis-shop-7.webp"
                  alt="ODIS factory diagnostic equipment at Paradise-Performance Audi repair shop"
                  className="w-full h-72 object-cover"
                />
              </div>
            </div>
            <p className="text-gray-600 text-sm mt-4 italic">
              Our shop runs genuine ODIS engineering software — the same system every Audi dealer uses for diagnostics, programming and online coding.
            </p>
          </motion.div>

          {/* Why Dallas Audi Owners Choose Us */}
          <motion.div {...fadeInUp} className="bg-gray-100 rounded-xl p-8 mb-16">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">Why Dallas Audi Owners Choose Our European Auto Repair Center</h2>
            <p className="text-lg text-gray-700 mb-6">
              Many Dallas Audi enthusiasts make the short drive to our Paradise, TX location for several compelling reasons:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />True expertise in Audi's unique systems and technologies</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />Significantly lower costs than dealership service (20-30% savings)</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />Direct communication with expert Audi technicians</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />Transparent diagnosis and repair recommendations</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />High-quality OEM parts options</li>
              <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />Focus on long-term reliability, not just quick fixes</li>
            </ul>
          </motion.div>

          {/* Customer Testimonial 2 */}
          <motion.div {...fadeInUp} className="bg-gray-100 rounded-xl p-8 mb-16 border-l-4 border-green-500">
            <p className="text-lg text-gray-700 italic mb-4">
              "As an Audi enthusiast with multiple vehicles, finding knowledgeable European auto specialists was crucial. After trying several places in Dallas, I discovered this shop in Paradise. Their understanding of the Quattro system and professional diagnostics exceeds even the dealership experience, but at much more reasonable prices. The drive from Dallas is absolutely worthwhile for anyone who truly cares about their Audi."
            </p>
            <p className="font-semibold text-gray-900">— Christopher R., Dallas</p>
          </motion.div>

          {/* Maintaining Your Audi in Dallas's Climate */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-gray-900">Maintaining Your Audi in Dallas's Climate</h2>
            <p className="text-lg text-gray-700 mb-8">
              The North Texas climate presents unique challenges for Audi vehicles that our European auto repair specialists address with customized maintenance plans:
            </p>
            
            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Summer Heat Protection</h3>
                <p className="text-gray-700">
                  Dallas's extreme summer temperatures can stress cooling systems, electronic components, and interior materials. Our European auto specialists provide enhanced cooling system services, battery testing optimized for heat, and interior protection treatments to preserve your Audi's comfort and reliability.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Stop-and-Go Traffic Considerations</h3>
                <p className="text-gray-700">
                  Dallas traffic can accelerate wear on Audi's advanced transmission systems, brakes, and engine components. Our enhanced service intervals and inspection protocols for European vehicles account for these conditions, catching developing issues before they lead to costly failures.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Extended Service Plans for Dallas Commuters</h3>
                <p className="text-gray-700">
                  For Dallas Audi owners who drive frequently, our European auto repair center offers customized maintenance plans that balance convenience and preventative care, with options for expedited service, loaner vehicles, and multiple-service discounts.
                </p>
              </div>
            </div>
          </motion.div>

          {/* FAQs Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-gray-900">Audi Repair: Frequently Asked Questions from Dallas Owners</h2>
            
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-xl font-bold mb-4 text-gray-900">How often does an Audi Quattro system need maintenance?</h3>
                <p className="text-gray-700">
                  While Audi's official recommendations vary by model, we advise Dallas drivers to service their Quattro differential fluids every 35,000-40,000 miles, especially if you experience heavy traffic or occasional performance driving. Regular inspections of CV boots, center differential functionality, and electronic torque distribution are also important for maintaining optimal Quattro performance.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-xl font-bold mb-4 text-gray-900">Are aftermarket parts safe for my Audi?</h3>
                <p className="text-gray-700">
                  Quality matters tremendously with European vehicles like Audi. While we offer both genuine Audi parts and select high-quality OEM alternatives, we never use generic aftermarket components that could compromise performance or reliability. Our European auto repair center carefully selects parts that meet or exceed manufacturer specifications, providing value without sacrificing quality.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-xl font-bold mb-4 text-gray-900">Why does my Audi require special synthetic oil?</h3>
                <p className="text-gray-700">
                  Audi engines are designed with precise tolerances, high operating temperatures, and specific lubrication requirements. The synthetic oils meeting Audi's VW 504.00/507.00 specifications provide critical protection for turbocharged engines, maintain proper viscosity through extreme Dallas temperature variations, and enable extended service intervals. Using incorrect oil can lead to premature wear, reduced efficiency, and potential warranty issues.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-xl font-bold mb-4 text-gray-900">Is the drive from Dallas to Paradise worth it for Audi service?</h3>
                <p className="text-gray-700">
                  Our Dallas clients consistently tell us that the short drive to our European auto repair center in Paradise is absolutely worthwhile. The combination of specialized expertise, significant cost savings compared to dealership service, and personalized attention creates value that justifies the brief trip. Many customers tell us they actually enjoy the quick drive outside the city and the opportunity to properly experience their Audi's driving dynamics.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Cross-link to Audi Inventory */}
          <motion.div {...fadeInUp} className="mb-12">
            <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-8 md:p-10">
              <div className="md:flex items-center gap-8">
                <div className="flex-1 mb-6 md:mb-0">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Looking to Buy an Audi?</h3>
                  <p className="text-gray-700">
                    We sell pre-owned Audis inspected by the same technicians who repair them. Every vehicle passes our full shop diagnostic before it hits the lot.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <Link
                    to="/inventory/audi"
                    className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700 transition"
                  >
                    Browse Audi Inventory <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Final CTA Section */}
          <motion.div {...fadeInUp} className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-6">Experience Superior Audi Care from European Auto Specialists</h2>
            <p className="text-xl mb-8">
              Our European auto repair team in Paradise provides specialized service for Dallas Audi owners who demand the best for their vehicles. Expert technicians and advanced diagnostic equipment ensure your Audi receives proper care.
            </p>
            
            <div className="space-y-2 mb-8">
              <p className="text-lg"><strong>Phone:</strong> <a href="tel:8173822316" className="underline">(817) 382-2316</a></p>
              <p className="text-lg"><strong>Address:</strong> 305 Co Rd 3470, Paradise, TX 76073</p>
              <p className="text-lg"><strong>Hours:</strong> Monday-Friday 8am-5PM, Saturday 9am-2pm</p>
            </div>
            
            <p className="text-lg mb-8">
              Trust your Audi servicing to the European auto repair specialists who understand its sophisticated engineering. From routine maintenance to complex diagnostics and performance enhancements, our team delivers expertise that keeps your Audi performing perfectly for all your Dallas driving needs.
            </p>
            
            <Link to="/contact" className="inline-block bg-white text-red-600 px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors font-bold text-lg">
              Schedule Audi Service Now
            </Link>
          </motion.div>

          {/* Related Services - Internal Linking for SEO */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-16 border-t border-gray-200 pt-8"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Related European Auto Repair Services in Dallas</h3>
            <p className="text-gray-700 mb-6">
              As part of our comprehensive <Link to="/services/european-car-repair" className="text-red-600 hover:underline font-semibold">European auto repair Dallas</Link> services, 
              we also specialize in servicing other premium German and European vehicles. Our Dallas-area customers trust us for:
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Link to="/services/european-car-repair/porsche" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition group">
                <h4 className="font-bold text-lg mb-2 group-hover:text-red-600">Porsche Repair Dallas</h4>
                <p className="text-gray-600 text-sm">IMS bearing experts, PIWIS diagnostics. 911, Cayenne, Boxster specialists.</p>
              </Link>
              <Link to="/services/european-car-repair/bmw" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition group">
                <h4 className="font-bold text-lg mb-2 group-hover:text-blue-600">BMW Repair Dallas</h4>
                <p className="text-gray-600 text-sm">Expert BMW service for all models. ISTA diagnostics, performance specialists.</p>
              </Link>
              <Link to="/services/european-car-repair/mercedes" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition group">
                <h4 className="font-bold text-lg mb-2 group-hover:text-gray-800">Mercedes Repair Dallas</h4>
                <p className="text-gray-600 text-sm">STAR diagnostics, AIRMATIC suspension, AMG performance service.</p>
              </Link>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/services/oil-change" className="text-red-600 hover:underline">Audi Oil Change Service</Link>
              <span className="text-gray-400">|</span>
              <Link to="/services/transmission-repair" className="text-red-600 hover:underline">DSG Transmission Service</Link>
              <span className="text-gray-400">|</span>
              <Link to="/services/suspension-repair" className="text-red-600 hover:underline">Audi Suspension Repair</Link>
              <span className="text-gray-400">|</span>
              <Link to="/services/european-car-repair" className="text-red-600 hover:underline">European Auto Repair Dallas</Link>
            </div>
          </motion.div>
          
        </div>
      </div>

      {/* Audi Vehicles For Sale */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <RelatedInventory make="Audi" limit={4} title="Audi Vehicles For Sale" />
      </div>
    </>
  );
};

export default AudiService;
